import { Alert, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { useOpenApiResources } from '../../../docs/openapi/useOpenApiResources'
import {
  getWorkspacePage,
  getWorkspaceSubPage,
  subPageLabel,
  subPagePath
} from '../../../navigation'
import { DocResourceView } from '../DocResourceView'
import styles from '../Docs.module.scss'

/**
 * API Reference: renders API docs generated live from the organisation's
 * OpenAPI document through `DocResourceView`. The sidebar's endpoint list is static (from the bundled snapshot, see
 * `navigation.ts`); only this page's content is live-fetched.
 */
export function ApiOpenApiPreviewPage() {
  const { t } = useTranslation()
  const { resourceId } = useParams<{ resourceId: string }>()
  const page = getWorkspacePage('api-openapi-preview')
  const subPage = getWorkspaceSubPage('api-openapi-preview', resourceId ?? '')
  const { resources, source, isPending, refetch } = useOpenApiResources()
  const resource = resources.find(r => r.id === resourceId)

  if (!subPage) {
    return <Navigate to={subPagePath(page, page.children![0])} replace />
  }

  return (
    <div className={styles.previewRoot} data-testid="dp-openapi-preview">
      {/* Only the cached-snapshot fallback is worth a banner (SAIV-11647). */}
      {source === 'snapshot' && (
        <Alert
          className={styles.previewBanner}
          type="warning"
          showIcon
          message={t('developerPlatform.docs.openapiPreview.bannerSnapshot', {
            count: resources.length
          })}
          action={
            <Button size="small" onClick={refetch} data-testid="dp-openapi-preview-refetch">
              {t('developerPlatform.docs.openapiPreview.refetch')}
            </Button>
          }
        />
      )}

      {isPending && (
        <div
          className={styles.previewLoading}
          aria-busy="true"
          data-testid="dp-openapi-preview-loading"
        >
          <span />
          <span />
          <span />
        </div>
      )}

      {!isPending && !resource && (
        <Alert type="warning" showIcon message={t('developerPlatform.docs.openapiPreview.empty')} />
      )}

      {resource && <DocResourceView resource={resource} title={subPageLabel(subPage, t)} />}
    </div>
  )
}
